import { useEffect, useState } from 'react'
import CreditCardInput from 'react-credit-card-input'
import { company, CustomerCouponCode, CustomerInvoiceData, CustomerReceiptData, CustomerReferralCode } from '../../utilities/CustomerData'
import { apiURL } from '../../utilities/URLs'
import { useNavigate, useParams } from 'react-router-dom'
import { PaymentConfirmation, PaymentDeclined } from './PaymentModals'
import posthog from 'posthog-js'

const MAX_FAILED_ATTEMPTS = 3
const LOCKOUT_TIME_MS = 60 * 60 * 1000

export function PublicPaymentCenter() {
  const navigate = useNavigate()
  const { paymentId } = useParams()
  const rateLimitKey = paymentId ? `public_pay_${paymentId.slice(0, 48)}` : 'public_pay'

  const [loadError, setLoadError] = useState('')
  const [loadingContext, setLoadingContext] = useState(true)
  const [leadData, setLeadData] = useState(null)
  const [customerInvoice, setCustomerInvoice] = useState(new CustomerInvoiceData())
  const [customerCouponCode, setCustomerCouponCode] = useState(new CustomerCouponCode())
  const [customerReferralCode, setCustomerReferralCode] = useState(new CustomerReferralCode())

  const [formData, setFormData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: ''
  })
  const [showCouponCode, setShowCouponCode] = useState(false)
  const [couponCode, setCouponCode] = useState('')
  const [couponError, setCouponError] = useState('')
  const [totalDiscountAmount, setTotalDiscountAmount] = useState('')
  const [errors, setErrors] = useState({})

  const [payingInvoice, setPayingInvoice] = useState(false)
  const [confirmPayment, setConfirmPayment] = useState(false)
  const [paymentDeclined, setPaymentDeclined] = useState(false)
  const [paymentError, setPaymentError] = useState('')
  const [paymentCode, setPaymentCode] = useState('')
  const [retriesRemaining, setRetriesRemaining] = useState(3)
  const [generatedReceipt, setGeneratedReceipt] = useState(new CustomerReceiptData())

  useEffect(() => {
    const load = async () => {
      setLoadingContext(true)
      setLoadError('')
      try {
        const r = await fetch(`${apiURL}/api/publicPaymentContext?paymentToken=${encodeURIComponent(paymentId)}`)
        const data = await r.json()
        if (data.response?.error) {
          setLoadError(typeof data.response.error === 'string' ? data.response.error : 'Unable to load this payment link.')
          setLeadData(null)
        } else {
          setLeadData(data.response.customerLeadData)
          setCustomerInvoice(new CustomerInvoiceData(data.response.customerInvoiceData))
          setCustomerCouponCode(new CustomerCouponCode(data.response.customerCouponCode))
          setCustomerReferralCode(new CustomerReferralCode(data.response.customerReferralCode))
        }
      } catch (e) {
        setLoadError('Unable to load this payment link. Please try again later.')
        setLeadData(null)
      }
      setLoadingContext(false)
    }
    if (paymentId)
      load()
  }, [paymentId])

  useEffect(() => {
    posthog.startSessionRecording()
    document.title = `${company.companyName} | Pay invoice`
    return () => posthog.stopSessionRecording()
  }, [])

  const handleCardNumberChange = (e) => {
    setFormData({ ...formData, cardNumber: e.target.value })
  }
  const handleCardExpiryChange = (e) => {
    setFormData({ ...formData, expiryDate: e.target.value })
  }
  const handleCardCVCChange = (e) => {
    setFormData({ ...formData, cvv: e.target.value })
  }

  const validateForm = () => {
    const newErrors = {}
    if (!formData.cardNumber) newErrors.cardNumber = 'Card Number is required'
    if (!formData.expiryDate) newErrors.expiryDate = 'Expiry Date is required'
    if (!formData.cvv) newErrors.cvv = 'CVV is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePaymentFailure = (payInvoiceData) => {
    const currentTime = new Date().getTime()
    let failedAttempts = parseInt(localStorage.getItem(`failedAttempts_${rateLimitKey}`) || '0', 10)
    let lastFailedAttempt = parseInt(localStorage.getItem(`lastFailed_${rateLimitKey}`) || '0', 10)

    failedAttempts++
    lastFailedAttempt = currentTime

    localStorage.setItem(`failedAttempts_${rateLimitKey}`, String(failedAttempts))
    localStorage.setItem(`lastFailed_${rateLimitKey}`, String(lastFailedAttempt))

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      setPaymentError('You have reached the maximum number of failed payment attempts. Please try again in 60 minutes.')
      setRetriesRemaining(0)
      setPaymentCode('')
      setPaymentDeclined(true)
    } else {
      setFormData({
        cardNumber: '',
        expiryDate: '',
        cvv: ''
      })
      setPaymentError(payInvoiceData.response.paymentResponse)
      setPaymentCode(payInvoiceData.response.paymentCode)
      setRetriesRemaining(3 - failedAttempts)
      setPaymentDeclined(true)
    }
  }

  const handlePaymentSuccess = (payInvoiceData) => {
    const bal = payInvoiceData.response.invoiceRemainingBalance
    setCustomerInvoice(prev => new CustomerInvoiceData({
      ...Object.assign({}, prev),
      invoiceRemainingBalance: bal.invoiceRemainingBalance,
      invoicePaid: bal.invoicePaid
    }))
    setGeneratedReceipt(new CustomerReceiptData(payInvoiceData.response.receiptInfo))
    setConfirmPayment(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const failedAttempts = parseInt(localStorage.getItem(`failedAttempts_${rateLimitKey}`) || '0', 10)
    const lastFailedAttempt = parseInt(localStorage.getItem(`lastFailed_${rateLimitKey}`) || '0', 10)
    const currentTime = new Date().getTime()

    if (failedAttempts >= MAX_FAILED_ATTEMPTS) {
      if (currentTime - lastFailedAttempt < LOCKOUT_TIME_MS) {
        setPaymentError(`You have reached the maximum number of failed payment attempts. Please try again in ${new Date(LOCKOUT_TIME_MS - (currentTime - lastFailedAttempt)).getMinutes()} minutes.`)
        setRetriesRemaining(0)
        setPaymentDeclined(true)
        return
      }
      localStorage.removeItem(`failedAttempts_${rateLimitKey}`)
      localStorage.removeItem(`lastFailed_${rateLimitKey}`)
    }

    if (!validateForm())
      return

    setPayingInvoice(true)
    const form = new FormData()
    form.append('paymentInfo', JSON.stringify({
      paymentToken: paymentId,
      paymentType: 'card',
      paymentAmount: customerInvoice.invoiceRemainingBalance,
      customerCreditCardInfo: {
        cardNumber: formData.cardNumber,
        cardExpiryDate: formData.expiryDate,
        cardCVV: formData.cvv
      }
    }))

    try {
      const payInvoiceResponse = await fetch(`${apiURL}/api/publicPayInvoice`, {
        method: 'POST',
        body: form
      })
      const payInvoiceData = await payInvoiceResponse.json()
      setPayingInvoice(false)

      if (payInvoiceData.response.error) {
        setPaymentError(typeof payInvoiceData.response.error === 'string' ? payInvoiceData.response.error : 'Payment could not be processed.')
        setPaymentCode('')
        setPaymentDeclined(true)
      } else if (payInvoiceData.response.paymentResponse === 'Payment Successful!')
        handlePaymentSuccess(payInvoiceData)
      else
        handlePaymentFailure(payInvoiceData)
    } catch (error) {
      setPayingInvoice(false)
      console.error('An error occurred while processing the payment:', error)
    }
  }

  const handleCheckCouponCode = async () => {
    const form = new FormData()
    form.append('couponInfo', JSON.stringify({
      paymentToken: paymentId,
      couponCode: couponCode.trim()
    }))

    const checkCouponResponse = await fetch(`${apiURL}/api/publicCheckCouponCode`, {
      method: 'POST',
      body: form
    })
    const checkCouponData = await checkCouponResponse.json()
    if (checkCouponData.response.error)
      setCouponError(checkCouponData.response.error)
    else {
      setCouponError('')
      setCouponCode('')
      setCustomerCouponCode(new CustomerCouponCode({
        customerId: leadData.leadContact.contactId,
        couponCode: checkCouponData.response.couponCode.couponCode,
        couponAmount: checkCouponData.response.couponCode.couponAmount,
        couponType: checkCouponData.response.couponCode.couponType
      }))
    }
  }

  const getNewInvoiceBalance = () => {
    if (customerCouponCode.couponAmount > 0 || customerReferralCode.numHaveRefund > 0 || customerReferralCode.appliedReferralCode.length > 0) {
      let discountAmount = 0
      if (customerCouponCode.couponAmount > 0)
        discountAmount += customerCouponCode.couponAmount > 1 ? customerCouponCode.couponAmount : (customerInvoice.invoiceRemainingBalance * customerCouponCode.couponAmount).toFixed(2)
      if (customerReferralCode.numHaveRefund > 0)
        discountAmount += customerReferralCode.numHaveRefund * 50
      if (customerReferralCode.appliedReferralCode.length > 0)
        discountAmount += 50

      if (Number((customerInvoice.invoiceBalanceTotal - discountAmount).toFixed(2)) > 0)
        setTotalDiscountAmount((customerInvoice.invoiceBalanceTotal - parseFloat(discountAmount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
      else
        setTotalDiscountAmount(0)
    }
  }

  useEffect(() => {
    getNewInvoiceBalance()
  }, [customerCouponCode, customerReferralCode])

  if (loadingContext) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[50vh] font-mont'>
        <div className='loader ease-linear rounded-full border-8 border-t-8 h-12 w-12 mb-4' />
        <p className='text-lg text-gray-600'>Loading payment details…</p>
      </div>
    )
  }

  if (loadError || !leadData) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[50vh] font-mont px-4'>
        <p className='text-lg text-gray-800 text-center max-w-md'>{loadError || 'This payment link is not valid.'}</p>
        <button type='button' onClick={() => navigate('/')} className='mt-6 px-6 py-2 bg-primaryColor text-white rounded-lg hover:bg-primaryDarkColor transition'>
          Return to sign in
        </button>
      </div>
    )
  }

  return (
    <>
      <div className='flex justify-center w-full px-4'>
        <div className='flex flex-col items-center max-w-5xl w-full'>
          <p className='text-center text-gray-600 mt-6 mb-2'>Secure payment — sign-in is not required for this link.</p>
          <p onClick={() => setShowCouponCode(!showCouponCode)} className='w-full p-4 text-white mt-4 rounded-lg shadow-lg cursor-pointer bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
            Have a coupon code? Click here to enter your code
          </p>
          <div className='p-4 w-full h-fit flex lg:flex-row flex-col mb-10 justify-center gap-4 font-mont'>
            <div className='mb-6 w-full lg:w-1/2'>
              <h3 className='text-xl font-medium mb-4'>Invoice details</h3>
              <div className='border p-6 rounded-md bg-gray-50'>
                <p className='text-lg font-semibold mb-2'>Invoice info</p>
                <p className='text-sm mb-2'><strong>Invoice #:</strong> {customerInvoice.invoiceNumber}</p>
                <p className='text-sm mb-2'><strong>Description:</strong> {customerInvoice.invoiceDescription}</p>
                <p className='text-sm mb-2'><strong>Refund:</strong> ${customerInvoice.invoiceRefund.toLocaleString()}</p>
                <p className='text-sm mb-2'><strong>Rate:</strong> {Math.round(Number(customerInvoice.invoiceRate) * 100)}%</p>
                <p className='text-sm mb-2'><strong>Sub total:</strong> ${customerInvoice.invoiceSubTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                <p className='text-sm mb-2'><strong>Tax:</strong> ${customerInvoice.invoiceTax.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                {customerInvoice.invoiceDiscount > 0 ? (
                  <div>
                    <div className='mb-4'>
                      <p className='text-sm mb-2'>
                        <strong>Balance total:</strong>
                        <span className='line-through text-gray-500 ml-2'>${customerInvoice.invoiceBalanceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600'>${(customerInvoice.invoiceRemainingBalance - (customerInvoice.invoiceRemainingBalance * customerInvoice.invoiceDiscount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                      <p className='text-sm mb-2'>
                        <strong>Remaining balance total:</strong>
                        <span className='line-through text-gray-500 ml-2'>${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600'>${(customerInvoice.invoiceRemainingBalance - (customerInvoice.invoiceRemainingBalance * customerInvoice.invoiceDiscount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                      <p className='text-xl mb-4'>
                        <strong>Paying today:</strong>
                        <span className='line-through text-gray-500 ml-2 font-bold'>${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        <span className='ml-4 text-green-600 font-bold'>${(customerInvoice.invoiceRemainingBalance - (customerInvoice.invoiceRemainingBalance * customerInvoice.invoiceDiscount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </p>
                    </div>
                  </div>
                ) : totalDiscountAmount.length === 0 ? (
                  <div>
                    <p className='text-sm mb-2'><strong>Balance total:</strong> ${customerInvoice.invoiceBalanceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className='text-sm mb-2'><strong>Remaining balance total:</strong> ${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    <p className='text-xl mb-4'><strong>Paying today: ${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></p>
                  </div>
                ) : (
                  <div>
                    <p className='text-sm mb-2'>
                      <strong>Balance total:</strong>
                      <span className='line-through text-gray-500 ml-2'>${customerInvoice.invoiceBalanceTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className='ml-4 text-green-600'>${totalDiscountAmount}</span>
                    </p>
                    <p className='text-xl mb-4'>
                      <strong>Paying today:</strong>
                      <span className='line-through text-gray-500 ml-2 font-bold'>${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className='ml-4 text-green-600 font-bold'>${totalDiscountAmount}</span>
                    </p>
                  </div>
                )}
                {showCouponCode && (
                  <div className='flex flex-col gap-4 mt-4'>
                    <div className='bg-blue-50 shadow-lg rounded-md p-4'>
                      <label htmlFor='publicCouponCode' className='block text-sm font-medium text-gray-700 mb-2'>
                        Apply coupon code
                      </label>
                      {couponError && <p className='text-red-500 text-xs mt-2'>{couponError}</p>}
                      {customerCouponCode.couponCode.length > 0 ? (
                        <div className='flex flex-row gap-4 items-center mt-4'>
                          <p>Applied coupon code:</p>
                          <p className='font-bold'>{customerCouponCode.couponCode}</p>
                        </div>
                      ) : (
                        <div className='flex items-center'>
                          <input
                            type='text'
                            id='publicCouponCode'
                            name='publicCouponCode'
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value)}
                            className='p-2 border border-gray-300 rounded-l-md w-full'
                            placeholder='Enter coupon code'
                          />
                          <button
                            type='button'
                            onClick={() => handleCheckCouponCode()}
                            className={`text-white p-2 rounded-r-md ${couponCode.length === 0 ? 'bg-gray-400 text-gray-200 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600 transition-colors'}`}
                            disabled={couponCode.length === 0}
                          >
                            Apply
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='max-w-md w-full p-6 bg-white shadow-lg rounded-lg h-fit'>
              <h2 className='text-2xl font-semibold mb-6'>Card payment</h2>
              <p className='text-sm text-gray-700 mb-4'>
                Paying full remaining balance:{' '}
                <span className='font-semibold'>
                  ${customerInvoice.invoiceRemainingBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </p>
              <div className='mb-4 flex flex-col items-center'>
                <label htmlFor='publicCardNumber' className='text-sm font-medium text-gray-700 text-start w-full'>Card number</label>
                <CreditCardInput
                  cardNumberInputProps={{
                    id: 'publicCardNumber',
                    value: formData.cardNumber,
                    onChange: handleCardNumberChange,
                  }}
                  cardExpiryInputProps={{
                    value: formData.expiryDate,
                    onChange: handleCardExpiryChange,
                  }}
                  cardCVCInputProps={{
                    value: formData.cvv,
                    onChange: handleCardCVCChange,
                  }}
                  fieldClassName='mt-2 p-2 border border-gray-300 rounded-md'
                />
                <div className='flex gap-4'>
                  {errors.cardNumber && <p className='text-red-500 text-xs mt-1'>{errors.cardNumber}</p>}
                  {errors.expiryDate && <p className='text-red-500 text-xs mt-1'>{errors.expiryDate}</p>}
                  {errors.cvv && <p className='text-red-500 text-xs mt-1'>{errors.cvv}</p>}
                </div>
              </div>
              <button
                type='submit'
                onClick={handleSubmit}
                className='w-full bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white p-2 rounded-md mt-2'
                disabled={payingInvoice}
              >
                {payingInvoice ? 'Processing…' : 'Submit payment'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <PaymentConfirmation
        isOpen={confirmPayment}
        onClose={() => navigate('/')}
        _customerReceipt={generatedReceipt}
        isShippo={false}
        isPublicPaymentLink
        publicPaymentToken={paymentId}
      />
      <PaymentDeclined
        isOpen={paymentDeclined}
        setIsOpen={setPaymentDeclined}
        paymentError={paymentError}
        paymentCode={paymentCode}
        retriesRemaining={retriesRemaining}
      />
    </>
  )
}
